import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';
import { studentPaymentApi } from '../Enrollment/Students/StudentPaymentApi';
import { studentsApi } from '../Enrollment/Students/StudentsApi';

export const ShopApi = createApi({
  reducerPath: 'ShopApi',
  baseQuery,
  tagTypes: ['SaleItems', 'Sales', 'Exemptions'],
  endpoints: builder => ({
    getSalesItems: builder.query<any, { isSubscription?: boolean } | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params && params.isSubscription !== undefined) {
          queryParams.append('is_subscription', String(params.isSubscription));
        }
        return `/finance/sales/items?${queryParams.toString()}`;
      },
      providesTags: ['SaleItems'],
    }),
    createSalesItem: builder.mutation<any, any>({
      query: newSalesItem => ({
        url: '/finance/sales/items',
        method: 'POST',
        body: newSalesItem,
      }),
      invalidatesTags: ['SaleItems'],
    }),
    updateSalesItem: builder.mutation<any, { id: number; updates: any }>({
      query: ({ id, updates }) => ({
        url: `/finance/sales/items/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['SaleItems'],
    }),
    deleteSalesItem: builder.mutation<void, number>({
      query: id => ({
        url: `/finance/sales/items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SaleItems'],
    }),
    reStockSalesItem: builder.mutation<any, { id: number; quantity: number }>({
      query: ({ id, quantity }) => ({
        url: `/finance/sales/items/${id}/restock`,
        method: 'POST',
        body: { quantity },
      }),
      invalidatesTags: ['SaleItems'],
    }),
    getSales: builder.query<
      any,
      { sessionId?: string; search?: string; page?: number; page_size?: number }
    >({
      query: ({ sessionId, search, page = 1, page_size = 25 }) => {
        const params = new URLSearchParams();
        if (sessionId) params.append('sessionId', sessionId);
        if (search) params.append('q', search);
        if (page) params.append('page', page.toString());
        if (page_size) params.append('page_size', page_size.toString());
        return `/finance/sales?${params.toString()}`;
      },
      providesTags: ['Sales'],
    }),
    createSale: builder.mutation<any, any>({
      query: newSale => ({
        // The endpoint is /finance/sales/ not /finance/sales/items
        url: '/finance/sales/',
        method: 'POST',
        body: newSale,
      }),
      invalidatesTags: ['Sales'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            studentPaymentApi.util.invalidateTags([
              'studentPayment',
              'studentSummary',
              'Student',
            ])
          );
          dispatch(studentsApi.util.invalidateTags(['Subscriptions']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    getSaleById: builder.query({
      query: id => `/finance/sales/${id}`,
      providesTags: ['Sales'],
    }),

    getSubscriptionExemptions: builder.query<any, {
      studentId?: string;
      itemId?: string;
      startDate?: string;
      endDate?: string;
    }>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.studentId) queryParams.append('studentId', params.studentId);
        if (params.itemId) queryParams.append('itemId', params.itemId);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        return `/finance/sales/subscriptions/exemptions?${queryParams.toString()}`;
      },
      providesTags: ['Exemptions'],
    }),

    createSubscriptionExemption: builder.mutation<any, any>({
      query: (exemption) => ({
        url: '/finance/sales/subscriptions/exemptions',
        method: 'POST',
        body: exemption,
      }),
      invalidatesTags: ['Exemptions'],
    }),

    deleteSubscriptionExemption: builder.mutation<void, number>({
      query: (id) => ({
        url: `/finance/sales/subscriptions/exemptions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exemptions'],
    }),
  }),
});

export const {
  useGetSalesItemsQuery,
  useCreateSalesItemMutation,
  useUpdateSalesItemMutation,
  useDeleteSalesItemMutation,
  useReStockSalesItemMutation,
  useGetSalesQuery,
  useCreateSaleMutation,
  useGetSaleByIdQuery,
  useGetSubscriptionExemptionsQuery,
  useCreateSubscriptionExemptionMutation,
  useDeleteSubscriptionExemptionMutation,
} = ShopApi;
